import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Spinner,
  Switch,
  Text,
} from "@chakra-ui/react";
import { LinkIcon, UserPlusIcon, XMarkIcon, CheckIcon } from "@heroicons/react/24/outline";
import { PALETTES } from "../appColors";
import { STANDE } from "../../StandeClassroom/standeTokens";
import { sharedPaperUrl, usePaperShare } from "./usePaperShare";

/** A filled button in the literal palette: the modal is portalled out of the
 *  themed page, so the CSS-variable tokens would fall back to light. */
const solid = (p) => ({
  bg: p.ink,
  color: p.onInk,
  border: "1px solid",
  borderColor: p.ink,
  _hover: { bg: p.ink, opacity: 0.86 },
  _active: { bg: p.ink, opacity: 0.8 },
});

const initialOf = (person) =>
  ((person.first_name || person.email || "?").trim()[0] || "?").toUpperCase();

const nameOf = (person) =>
  [person.first_name, person.last_name].filter(Boolean).join(" ") || person.email;

/**
 * Share a paper (from the Share button beside Test Mode).
 *
 * Two ways, one above the other, in the order people reach for them:
 *
 * - The link. Off until switched on; while on, anyone with it can open the
 *   paper and read it — answers, marks once released, and the board — but
 *   change nothing. Switching it off stops the link working without
 *   changing it, so switching back on revives links already handed out.
 * - Collaborators. Anyone with a Stande account, added by email. They open
 *   the paper through the same link, whether or not it is switched on, and
 *   can draw on the board with you. The answers stay yours.
 */
const ShareModal = ({ isOpen, onClose, attemptGuid, paperName, isDark = false }) => {
  const p = isDark ? PALETTES.dark : PALETTES.light;
  const share = usePaperShare({ attemptGuid, enabled: isOpen });
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [switching, setSwitching] = useState(false);

  const state = share.state;
  const link = state ? sharedPaperUrl(state.link_token) : "";

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      // Older browsers: select it so the person can copy it themselves.
      document.getElementById("stande-share-link")?.select();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const toggleLink = async (on) => {
    setSwitching(true);
    try {
      await share.setLinkEnabled(on);
    } catch (e) {
      setAddError(e.message);
    } finally {
      setSwitching(false);
    }
  };

  const add = async (e) => {
    e?.preventDefault();
    const value = email.trim();
    if (!value) return;
    setAdding(true);
    setAddError(null);
    try {
      await share.addCollaborator(value);
      setEmail("");
    } catch (err) {
      setAddError(err.message);
    } finally {
      setAdding(false);
    }
  };

  const labelStyle = { fontSize: "12px", fontWeight: 600, color: p.text, letterSpacing: "0.01em" };
  const noteStyle = { fontSize: "12px", color: p.textSub, lineHeight: "1.45" };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg={p.overlay} />
      <ModalContent
        bg={p.page}
        color={p.text}
        borderRadius="18px"
        border="1px solid"
        borderColor={p.border}
        boxShadow="0 24px 60px -24px rgba(0,0,0,0.35)"
        mx="16px"
      >
        <ModalCloseButton top="16px" right="16px" borderRadius="full" color={p.textSub} />
        <ModalBody px="24px" pt="24px" pb="24px">
          <Text fontSize="17px" fontWeight={600} color={p.text} pr="32px">
            Share this paper
          </Text>
          {paperName && (
            <Text fontSize="13px" color={p.textSub} mt="2px" noOfLines={1} pr="32px">
              {paperName}
            </Text>
          )}

          {!state && (
            <Flex align="center" justify="center" py="40px" gap="10px" color={p.textSub}>
              {share.error ? (
                <Text fontSize="13px" color={p.badText}>
                  {share.error}
                </Text>
              ) : (
                <>
                  <Spinner size="sm" />
                  <Text fontSize="13px">Getting your share settings…</Text>
                </>
              )}
            </Flex>
          )}

          {state && (
            <>
              {/* The link */}
              <Box mt="20px" p="16px" borderRadius="14px" bg={p.panel} border="1px solid" borderColor={p.borderSoft}>
                <Flex align="center" gap="12px">
                  <Flex
                    boxSize="34px"
                    borderRadius="10px"
                    align="center"
                    justify="center"
                    bg={state.link_enabled ? STANDE.primaryBlue : p.greySurface}
                    color={state.link_enabled ? STANDE.black : p.textSub}
                    flexShrink={0}
                    transition="background 0.15s"
                  >
                    <Icon as={LinkIcon} boxSize="16px" />
                  </Flex>
                  <Box flex="1" minW={0}>
                    <Text {...labelStyle}>Anyone with the link</Text>
                    <Text {...noteStyle}>
                      {state.link_enabled
                        ? "Can open this paper and read it. Nothing can be changed."
                        : "Off. Only you and your collaborators can open it."}
                    </Text>
                  </Box>
                  <Switch
                    aria-label="Share with anyone who has the link"
                    isChecked={state.link_enabled}
                    isDisabled={switching}
                    onChange={(e) => toggleLink(e.target.checked)}
                    sx={{
                      "span.chakra-switch__track": { bg: p.border },
                      "span.chakra-switch__track[data-checked]": { bg: p.ink },
                      "span.chakra-switch__thumb": { bg: p.page },
                    }}
                  />
                </Flex>

                <Flex mt="14px" gap="8px">
                  <Input
                    id="stande-share-link"
                    value={link}
                    isReadOnly
                    onFocus={(e) => e.target.select()}
                    h="38px"
                    fontSize="12.5px"
                    borderRadius="10px"
                    bg={p.page}
                    color={state.link_enabled ? p.text : p.textMuted}
                    borderColor={p.border}
                    _hover={{ borderColor: p.border }}
                    _focusVisible={{ borderColor: p.ink, boxShadow: "none" }}
                  />
                  <Button
                    {...solid(p)}
                    h="38px"
                    px="14px"
                    borderRadius="10px"
                    fontSize="13px"
                    fontWeight={500}
                    flexShrink={0}
                    onClick={copy}
                    leftIcon={copied ? <Icon as={CheckIcon} boxSize="14px" /> : undefined}
                  >
                    {copied ? "Copied" : "Copy link"}
                  </Button>
                </Flex>
              </Box>

              {/* Collaborators */}
              <Box mt="16px" p="16px" borderRadius="14px" bg={p.panel} border="1px solid" borderColor={p.borderSoft}>
                <Flex align="center" gap="12px">
                  <Flex
                    boxSize="34px"
                    borderRadius="10px"
                    align="center"
                    justify="center"
                    bg={p.greySurface}
                    color={p.textSub}
                    flexShrink={0}
                  >
                    <Icon as={UserPlusIcon} boxSize="16px" />
                  </Flex>
                  <Box flex="1" minW={0}>
                    <Text {...labelStyle}>Collaborators</Text>
                    <Text {...noteStyle}>
                      Anyone with a Stande account. They open the paper from the link and can draw on it with you.
                      Your answers stay yours.
                    </Text>
                  </Box>
                </Flex>

                <Flex as="form" onSubmit={add} mt="14px" gap="8px">
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (addError) setAddError(null);
                    }}
                    placeholder="Their Stande email"
                    h="38px"
                    fontSize="13px"
                    borderRadius="10px"
                    bg={p.page}
                    color={p.text}
                    borderColor={addError ? p.badText : p.border}
                    _placeholder={{ color: p.textMuted }}
                    _hover={{ borderColor: addError ? p.badText : p.border }}
                    _focusVisible={{ borderColor: p.ink, boxShadow: "none" }}
                  />
                  <Button
                    type="submit"
                    {...solid(p)}
                    h="38px"
                    px="16px"
                    borderRadius="10px"
                    fontSize="13px"
                    fontWeight={500}
                    flexShrink={0}
                    isLoading={adding}
                    isDisabled={!email.trim()}
                  >
                    Add
                  </Button>
                </Flex>
                {addError && (
                  <Text fontSize="12px" color={p.badText} mt="6px" role="alert">
                    {addError}
                  </Text>
                )}

                <Box mt="12px">
                  <Person p={p} initial="Y" name="You" detail="Owner" />
                  {state.collaborators.map((person) => (
                    <Person
                      key={person.user_guid}
                      p={p}
                      initial={initialOf(person)}
                      name={nameOf(person)}
                      detail={person.email}
                      onRemove={() => share.removeCollaborator(person.user_guid).catch((e) => setAddError(e.message))}
                    />
                  ))}
                </Box>
              </Box>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const Person = ({ p, initial, name, detail, onRemove }) => (
  <Flex align="center" gap="10px" py="7px">
    <Flex
      boxSize="28px"
      borderRadius="full"
      align="center"
      justify="center"
      bg={p.greySurface}
      color={p.text}
      fontSize="11.5px"
      fontWeight={600}
      flexShrink={0}
    >
      {initial}
    </Flex>
    <Box flex="1" minW={0}>
      <Text fontSize="13px" color={p.text} noOfLines={1}>
        {name}
      </Text>
      <Text fontSize="11.5px" color={p.textMuted} noOfLines={1}>
        {detail}
      </Text>
    </Box>
    {onRemove && (
      <Flex
        as="button"
        type="button"
        aria-label={`Remove ${name}`}
        boxSize="26px"
        borderRadius="full"
        align="center"
        justify="center"
        color={p.textMuted}
        _hover={{ bg: p.hover, color: p.text }}
        onClick={onRemove}
      >
        <Icon as={XMarkIcon} boxSize="14px" />
      </Flex>
    )}
  </Flex>
);

export default ShareModal;
